import { Popover as BasePopover } from "@base-ui/react/popover";
import { useState, type FunctionComponent, type ReactElement, type ReactNode } from "react";
import { cn } from "./cn";
import { POPUP_TRANSITION, POPUP_Z_INDEX, toPositioning, type Placement } from "./popup";

export type PopupTrigger = "hover" | "click";

export interface PopoverProps {
  content: ReactNode;
  title?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  placement?: Placement;
  /** Defaults to "hover", as in antd. An antd-style array (`["click"]`) is accepted too. */
  trigger?: PopupTrigger | PopupTrigger[];
  /** Hover delay in seconds (antd `mouseEnterDelay`). */
  mouseEnterDelay?: number;
  /** Set to false when the child is not a native `<button>` (e.g. a `<div>` or `<a>`). */
  nativeButton?: boolean;
  /** Popup class. */
  className?: string;
  /** The trigger — must accept a ref and spread props. */
  children: ReactElement;
}

/** Floating panel with an optional title, anchored to a single child (Base UI `Popover`). */
export const Popover: FunctionComponent<PopoverProps> = ({
  content,
  title,
  open,
  onOpenChange,
  placement = "top",
  trigger = "hover",
  mouseEnterDelay = 0.1,
  nativeButton = true,
  className,
  children,
}) => {
  const { side, align } = toPositioning(placement);
  const triggers = ([] as PopupTrigger[]).concat(trigger);
  const openOnHover = triggers.includes("hover");
  const hoverOnly = openOnHover && !triggers.includes("click");

  // Owned here so a press on a hover-only trigger can close the popup: Base UI would otherwise pin it open,
  // leaving it covering the page after a click that navigates or acts (e.g. citation links).
  const [innerOpen, setInnerOpen] = useState(false);
  const handleOpenChange = (next: boolean, details: { reason: string }) => {
    const resolved = hoverOnly && details.reason === "trigger-press" ? false : next;
    setInnerOpen(resolved);
    onOpenChange?.(resolved);
  };

  return (
    <BasePopover.Root open={open ?? innerOpen} onOpenChange={handleOpenChange}>
      <BasePopover.Trigger
        openOnHover={openOnHover}
        delay={mouseEnterDelay * 1000}
        closeDelay={100}
        nativeButton={nativeButton}
        render={children}
      />
      <BasePopover.Portal>
        <BasePopover.Positioner side={side} align={align} sideOffset={8} className={POPUP_Z_INDEX}>
          <BasePopover.Popup
            className={cn(
              "max-w-[min(90vw,400px)] rounded-lg bg-noa-elevated p-3 text-sm text-noa shadow-lg shadow-noa-shadow outline-none",
              POPUP_TRANSITION,
              className
            )}
          >
            {title && (
              <BasePopover.Title className="mt-0 mb-2 text-sm font-semibold text-noa-strong">{title}</BasePopover.Title>
            )}
            {content}
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>
  );
};
